import { COHORTS, getCohort } from './cohorts';
import { getPerson } from './people';
import { createRng } from './rng';
import { ROLES, type Cohort, type Person, type PersonId, type Role, type Roster } from './types';

export const TOTAL_ROUNDS = 6;
export const STARTING_REROLLS = 2;

/**
 * Every cohort draw is derived from `seed` + `drawIndex`, so a whole game is
 * reproducible from its seed alone. Shareable runs and daily challenges are
 * then just "start with this seed".
 */
export type GameState = {
  seed: string;
  /** Which slot we're filling, 1..6. */
  round: number;
  /** Increments on every cohort draw — a reroll counts, a round counts. */
  drawIndex: number;
  cohortId: string;
  /** Most recent cohorts, newest first — kept so draws don't repeat themselves. */
  recentCohortIds: string[];
  roster: Roster;
  rerollsLeft: number;
  drafted: PersonId[];
};

const MIN_POOL = 3;
/** How many recent cohorts to avoid re-drawing. */
const AVOID_RECENT = 2;

const availableIn = (cohort: Cohort, drafted: PersonId[]) =>
  cohort.members.filter((id) => !drafted.includes(id));

const drawCohort = (seed: string, drawIndex: number, drafted: PersonId[], avoid: string[]): string => {
  const rng = createRng(`${seed}:cohort:${drawIndex}`);
  const fresh = COHORTS.filter(
    (cohort) => !avoid.includes(cohort.id) && availableIn(cohort, drafted).length >= MIN_POOL,
  );
  const pool = fresh.length > 0 ? fresh : COHORTS.filter((c) => availableIn(c, drafted).length >= 2);
  return rng.pick(pool.length > 0 ? pool : COHORTS).id;
};

/** The cohorts a fresh draw should skip, newest first. */
const avoidList = (state: GameState) =>
  [state.cohortId, ...state.recentCohortIds].slice(0, AVOID_RECENT);

export const newGame = (seed = `${Date.now()}-${Math.floor(Math.random() * 1e6)}`): GameState => ({
  seed,
  round: 1,
  drawIndex: 0,
  cohortId: drawCohort(seed, 0, [], []),
  recentCohortIds: [],
  roster: {},
  rerollsLeft: STARTING_REROLLS,
  drafted: [],
});

/** The cohort members still on the board, in a stable shuffled order. */
export const cohortLineup = (state: GameState): Person[] => {
  const cohort = getCohort(state.cohortId);
  const rng = createRng(`${state.seed}:lineup:${state.drawIndex}`);
  return rng.shuffle(availableIn(cohort, state.drafted)).map(getPerson);
};

export const openRoles = (state: GameState): Role[] => ROLES.filter((role) => !state.roster[role]);

export const isComplete = (state: GameState): boolean => openRoles(state).length === 0;

export const draft = (state: GameState, personId: PersonId, role: Role): GameState => {
  if (state.roster[role]) return state;

  const drafted = [...state.drafted, personId];
  const roster: Roster = {
    ...state.roster,
    [role]: { role, personId, cohortId: state.cohortId, round: state.round },
  };
  const drawIndex = state.drawIndex + 1;
  const done = ROLES.every((r) => roster[r]);

  return {
    ...state,
    roster,
    drafted,
    round: Math.min(state.round + 1, TOTAL_ROUNDS),
    drawIndex,
    cohortId: done ? state.cohortId : drawCohort(state.seed, drawIndex, drafted, avoidList(state)),
    recentCohortIds: done ? state.recentCohortIds : avoidList(state),
  };
};

/** Swaps the cohort. Deliberately does *not* advance the round. */
export const reroll = (state: GameState): GameState => {
  if (state.rerollsLeft <= 0) return state;
  const drawIndex = state.drawIndex + 1;
  return {
    ...state,
    drawIndex,
    rerollsLeft: state.rerollsLeft - 1,
    cohortId: drawCohort(state.seed, drawIndex, state.drafted, avoidList(state)),
    recentCohortIds: avoidList(state),
  };
};
